import { useState, useEffect, useCallback } from 'react'
import { Plus, Trash2, GripVertical, ArrowUp, ArrowDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { useAuth } from '../App'
import { showSuccessToast, showErrorToast } from '@/components/NotificationToast'
import { aiInstructionsApi, type AIInstruction } from '@/services/promptService'

export function AIInstructionsPage() {
  const { isAdmin } = useAuth()
  const [instructions, setInstructions] = useState<AIInstruction[]>([])
  const [loading, setLoading] = useState(true)
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [newContent, setNewContent] = useState('')
  const [creating, setCreating] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editContent, setEditContent] = useState('')
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)

  const fetchInstructions = useCallback(async () => {
    try {
      const data = await aiInstructionsApi.list()
      setInstructions(data)
    } catch {
      showErrorToast('Failed to load AI instructions')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchInstructions()
  }, [fetchInstructions])

  if (!isAdmin()) {
    return (
      <div className="rounded-lg border border-neutral-200 bg-white p-8 text-center">
        <h2 className="text-2xl font-bold text-red-500 mb-4">Access Denied</h2>
        <p className="text-gray-500">You don't have permission to access this page.</p>
      </div>
    )
  }

  const handleCreate = async () => {
    if (!newContent.trim()) return
    setCreating(true)
    try {
      const maxOrder = instructions.reduce((max, i) => Math.max(max, i.sort_order), -1)
      await aiInstructionsApi.create({
        content: newContent.trim(),
        sort_order: maxOrder + 1,
      })
      showSuccessToast('Instruction created')
      setNewContent('')
      setAddDialogOpen(false)
      await fetchInstructions()
    } catch {
      showErrorToast('Failed to create instruction')
    } finally {
      setCreating(false)
    }
  }

  const handleToggle = async (instruction: AIInstruction) => {
    try {
      await aiInstructionsApi.update(instruction.id, {
        is_active: !instruction.is_active,
      })
      setInstructions(prev =>
        prev.map(i => i.id === instruction.id ? { ...i, is_active: !i.is_active } : i)
      )
    } catch {
      showErrorToast('Failed to update instruction')
    }
  }

  const handleSaveEdit = async (id: string) => {
    if (!editContent.trim()) return
    try {
      await aiInstructionsApi.update(id, { content: editContent.trim() })
      setEditingId(null)
      showSuccessToast('Instruction updated')
      await fetchInstructions()
    } catch {
      showErrorToast('Failed to update instruction')
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await aiInstructionsApi.delete(id)
      setDeleteConfirmId(null)
      showSuccessToast('Instruction deleted')
      await fetchInstructions()
    } catch {
      showErrorToast('Failed to delete instruction')
    }
  }

  const handleMove = async (index: number, direction: 'up' | 'down') => {
    const swapIndex = direction === 'up' ? index - 1 : index + 1
    if (swapIndex < 0 || swapIndex >= instructions.length) return

    const reordered = [...instructions]
    const [moved] = reordered.splice(index, 1)
    reordered.splice(swapIndex, 0, moved)

    const items = reordered.map((item, i) => ({ id: item.id, sort_order: i }))
    setInstructions(reordered.map((item, i) => ({ ...item, sort_order: i })))

    try {
      await aiInstructionsApi.reorder(items)
    } catch {
      showErrorToast('Failed to reorder')
      await fetchInstructions()
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground mb-2 page-main-heading">AI Instructions</h2>
          <p className="text-sm text-muted-foreground">
            Instructions are appended to the invoice extraction prompt for all clients. Use them to guide
            how the AI processes invoices (e.g., "For all VAT IDs starting with GB, remove the GB part").
          </p>
        </div>
        <Button onClick={() => setAddDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Instruction
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading...</div>
      ) : instructions.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-muted-foreground mb-4">No AI instructions yet.</p>
          <Button variant="outline" onClick={() => setAddDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add your first instruction
          </Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {instructions.map((instruction, index) => (
            <Card
              key={instruction.id}
              className={`p-4 ${!instruction.is_active ? 'opacity-60' : ''}`}
            >
              <div className="flex items-center gap-3">
                <div className="flex flex-col gap-1 pt-1">
                  <button
                    onClick={() => handleMove(index, 'up')}
                    disabled={index === 0}
                    className="text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ArrowUp className="h-3.5 w-3.5" />
                  </button>
                  <GripVertical className="h-3.5 w-3.5 text-muted-foreground/50" />
                  <button
                    onClick={() => handleMove(index, 'down')}
                    disabled={index === instructions.length - 1}
                    className="text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ArrowDown className="h-3.5 w-3.5" />
                  </button>
                </div>

                <div className="flex-1 min-w-0">
                  {editingId === instruction.id ? (
                    <div className="space-y-2">
                      <textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        autoFocus
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => handleSaveEdit(instruction.id)}>Save</Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <p
                      className="text-sm whitespace-pre-wrap cursor-pointer hover:bg-muted/50 rounded p-1 -m-1"
                      onClick={() => {
                        setEditingId(instruction.id)
                        setEditContent(instruction.content)
                      }}
                    >
                      {instruction.content}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => handleToggle(instruction)}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${instruction.is_active ? 'bg-primary' : 'bg-muted'
                      }`}
                    title={instruction.is_active ? 'Active - click to disable' : 'Inactive - click to enable'}
                  >
                    <span
                      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${instruction.is_active ? 'translate-x-4.5' : 'translate-x-1'
                        }`}
                    />
                  </button>
                  <button
                    onClick={() => setDeleteConfirmId(instruction.id)}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add Instruction Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add AI Instruction</DialogTitle>
            <DialogDescription>
              This instruction will be appended to the extraction prompt for all invoice processing.
            </DialogDescription>
          </DialogHeader>
          <textarea
            value={newContent}
            onChange={(e) => setNewContent(e.target.value)}
            placeholder="e.g., For all VAT IDs starting with GB, remove the GB prefix"
            className="w-full min-h-[120px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating || !newContent.trim()}>
              {creating ? 'Creating...' : 'Add Instruction'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Instruction</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this instruction? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
